import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { useBackOfficeAccess } from "@/features/auth/back-office-access";
import { usePreferredCompany } from "@/features/clients/hooks/use-preferred-company";
import {
  ArrowLeft,
  BrainIcon,
  BuildingsIcon,
  ChartLineUpIcon,
  CoinsIcon,
  ReceiptIcon,
  DatabaseIcon,
  GaugeIcon,
  ListMagnifyingGlassIcon,
  MicrophoneSlashIcon,
  PhoneCallIcon,
  PlugsConnectedIcon,
  PulseIcon,
  BroadcastIcon,
  RobotIcon,
  StackIcon,
  TestTubeIcon,
  UserPlusIcon,
  UsersIcon,
  UsersThreeIcon,
  WaveformIcon,
  WrenchIcon,
} from "@phosphor-icons/react";
import * as React from "react";
import { Link } from "react-router-dom";
import { NavSecondary, NavUser, type NavItem } from "../backoffice/sidebar";
import { ADMIN_HOME, adminSectionsFor } from "./nav";

/**
 * The back-office's own menu.
 *
 * The four subjects come from `ADMIN_SECTIONS` (`nav.tsx`) — the same declaration the
 * area was already organised around, now rendered as a rail instead of two rows
 * of tabs. An application with its own sidebar can show every subject and every
 * page at once, which is what the tab bar could not: it only ever showed the
 * pages of the subject you were already in.
 *
 * Only the icons live here, keyed by page: they are presentation, and putting
 * them in the declaration would make a routing file import an icon set.
 */

/** Icon per page of `ADMIN_SECTIONS` (`nav.tsx`), keyed by its route. */
const PAGE_ICONS: Record<string, NavItem["icon"]> = {
  clients: BuildingsIcon,
  "number-requests": PhoneCallIcon,
  provisioning: UserPlusIcon,
  telephony: PlugsConnectedIcon,
  live: BroadcastIcon,
  dashboard: GaugeIcon,
  sessions: ListMagnifyingGlassIcon,
  technical: WaveformIcon,
  stability: PulseIcon,
  "synthetic-calls": TestTubeIcon,
  "media-health": MicrophoneSlashIcon,
  "external-calls": ChartLineUpIcon,
  "tool-errors": WrenchIcon,
  costs: ReceiptIcon,
  credits: CoinsIcon,
  // A page without an icon is drawn as a sub-item (indented, 13px) by NavSecondary.
  "context-pressure": BrainIcon,
};

/**
 * Entities pinned under "Données": the handful support actually starts from.
 * The other forty are one click away, on the explorer's index.
 */
const PINNED_ENTITIES: {
  table: string;
  title: string;
  icon: NavItem["icon"];
}[] = [
  { table: "users", title: "Utilisateurs", icon: UsersIcon },
  { table: "customers", title: "Contacts", icon: UsersThreeIcon },
  { table: "sessions", title: "Sessions", icon: PhoneCallIcon },
  { table: "client_agents", title: "Agents", icon: RobotIcon },
  {
    table: "sessions_connectors",
    title: "Connecteurs",
    icon: PlugsConnectedIcon,
  },
];

export function AdminSidebar({
  ...props
}: React.ComponentProps<typeof Sidebar>) {
  const { preferredCompanyId } = usePreferredCompany();
  // The read-only account (sales) gets its pages only, and no explorer: the
  // rows themselves are an engineer's tool, and the server refuses it them.
  const access = useBackOfficeAccess() ?? "read";

  const sections: { label?: string; items: NavItem[] }[] = [
    {
      items: [
        {
          title: ADMIN_HOME.label,
          url: `/admin/${ADMIN_HOME.to}`,
          icon: PAGE_ICONS[ADMIN_HOME.to],
        },
      ],
    },
    ...adminSectionsFor(access).map((section) => ({
      label: section.label,
      items: section.pages.map((page) => ({
        title: page.label,
        url: `/admin/${page.to}`,
        icon: PAGE_ICONS[page.to],
      })),
    })),
    ...(access === "admin"
      ? [
          {
            label: "Données",
            items: [
              {
                title: "Explorateur",
                url: "/admin/explorer",
                icon: DatabaseIcon,
              },
              ...PINNED_ENTITIES.map((entity) => ({
                title: entity.title,
                url: `/admin/explorer/${entity.table}`,
                icon: entity.icon,
              })),
            ],
          },
        ]
      : []),
  ];

  return (
    <Sidebar collapsible="offcanvas" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to="/admin/live">
                <div className="bg-primary/10 text-primary flex aspect-square size-8 items-center justify-center rounded-lg">
                  <StackIcon weight="fill" className="size-5" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">Back-office</span>
                  <span className="text-muted-foreground truncate text-xs">
                    {access === "admin"
                      ? "Administration plateforme"
                      : "Lecture seule"}
                  </span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {sections.map((section) => (
          <SidebarGroup key={section.label ?? "home"} className="py-0">
            {section.label ? (
              <SidebarGroupLabel>{section.label}</SidebarGroupLabel>
            ) : null}
            <SidebarGroupContent>
              <NavSecondary items={section.items} />
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              {/* Back to the tenant application the staff member came from. */}
              <Link
                to={
                  preferredCompanyId ? `/${preferredCompanyId}/home` : "/home"
                }
              >
                <ArrowLeft weight="bold" />
                <span>Retour à l'application</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  );
}
